'use client';

/**
 * MatrixGridField — Likert-scale / matrix grid (rows × columns).
 *
 * Config via field.metadata:
 *   rows: Array<{ key: string; label: string }>
 *   columns: Array<{ key: string; label: string }>
 *   input_type: 'radio' | 'checkbox' (default 'radio')
 *
 * Value stored as Record<rowKey, columnKey | columnKey[]>.
 */

import React from 'react';
import { asObject, type FieldComponentField, type FieldValue } from '../formFieldTypes';

interface MatrixRow { key: string; label: string }
interface MatrixCol { key: string; label: string }

interface MatrixGridFieldProps {
  field: FieldComponentField;
  value: FieldValue;
  onChange: (value: Record<string, string | string[]>) => void;
  isDark: boolean;
  readOnly?: boolean;
}

export const MatrixGridField: React.FC<MatrixGridFieldProps> = ({
  field, value, onChange, isDark, readOnly,
}) => {
  const meta = field.metadata as {
    rows?: MatrixRow[];
    columns?: MatrixCol[];
    input_type?: 'radio' | 'checkbox';
  } | undefined;
  const optionBag = asObject(field.options) as { rows?: MatrixRow[]; columns?: MatrixCol[] } | null;
  const rows: MatrixRow[] = meta?.rows || optionBag?.rows || [];
  const cols: MatrixCol[] = meta?.columns || optionBag?.columns || [];
  const inputType: 'radio' | 'checkbox' = meta?.input_type || 'radio';
  const current: Record<string, string | string[]> = typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, string | string[]>) : {};

  if (!rows.length || !cols.length) {
    return (
      <div className={`text-sm p-3 rounded ${isDark ? 'bg-yellow-900/20 text-yellow-200' : 'bg-yellow-50 text-yellow-700'}`}>
        Matrix field &quot;{field.label}&quot; has no rows or columns configured.
      </div>
    );
  }

  const handleRadio = (rowKey: string, colKey: string) => {
    onChange({ ...current, [rowKey]: colKey });
  };

  const handleCheckbox = (rowKey: string, colKey: string) => {
    const existing = Array.isArray(current[rowKey]) ? current[rowKey] : [];
    const next = existing.includes(colKey)
      ? existing.filter((k: string) => k !== colKey)
      : [...existing, colKey];
    onChange({ ...current, [rowKey]: next });
  };

  const cellBorder = isDark ? 'border-gray-700' : 'border-gray-200';
  const headerBg = isDark ? 'bg-gray-800' : 'bg-gray-50';

  return (
    <div className="overflow-x-auto -mx-1">
      <table className={`w-full text-sm border-collapse border ${cellBorder} rounded-lg`}>
        <thead>
          <tr className={headerBg}>
            <th className={`px-3 py-2 text-left font-semibold border ${cellBorder}`} />
            {cols.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 text-center font-semibold border ${cellBorder} whitespace-nowrap`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => {
            const rowBg = ri % 2 === 0 ? '' : isDark ? 'bg-gray-800/50' : 'bg-gray-50/60';
            return (
              <tr key={row.key} className={rowBg}>
                <td className={`px-3 py-2.5 font-medium border ${cellBorder}`}>
                  {row.label}
                </td>
                {cols.map((col) => {
                  const checked =
                    inputType === 'radio'
                      ? current[row.key] === col.key
                      : Array.isArray(current[row.key]) && current[row.key].includes(col.key);
                  return (
                    <td key={col.key} className={`px-3 py-2.5 text-center border ${cellBorder}`}>
                      <input
                        type={inputType}
                        name={`matrix-${field.key}-${row.key}`}
                        checked={checked}
                        disabled={readOnly}
                        onChange={() =>
                          inputType === 'radio'
                            ? handleRadio(row.key, col.key)
                            : handleCheckbox(row.key, col.key)
                        }
                        className={`h-4 w-4 ${
                          inputType === 'radio'
                            ? 'text-blue-600 focus:ring-blue-500'
                            : 'text-blue-600 focus:ring-blue-500 rounded'
                        } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
                      />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
